import React, { useState, useEffect, useRef } from 'react';
import { X, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { contentAPI } from '../utils/api';

const NewsletterModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copy, setCopy] = useState({});
  const nameInputRef = useRef(null);

  useEffect(() => {
    contentAPI.getNewsletterCopy().then((d) => setCopy(d || {}));
  }, []);

  // When the modal opens, lock background scroll and auto-focus the name
  // field so the on-screen keyboard appears immediately (improves mobile UX
  // and prevents the dead-zone tap issue on small screens).
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => {
      try { nameInputRef.current?.focus(); } catch (e) { /* ignore */ }
    }, 50);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await contentAPI.subscribeNewsletter({ email, name, source: 'modal' });
      if (res.already) {
        toast.info('You are already subscribed.');
      } else {
        toast.success(copy.success_message || 'Successfully subscribed to our newsletter!');
      }
      setEmail('');
      setName('');
      onClose();
    } catch (err) {
      toast.error('Could not subscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center sm:items-center p-4 pt-10 sm:pt-4 overflow-y-auto bg-black/70 backdrop-blur-sm"
      data-testid="newsletter-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl max-w-md w-full p-6 sm:p-8 relative animate-fade-in my-auto shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors" aria-label="Close" data-testid="newsletter-close-btn">
          <X size={24} />
        </button>

        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-[#C9A86C] rounded-full flex items-center justify-center">
            <Mail size={32} className="text-white" />
          </div>
        </div>

        <h2 className="text-3xl text-center mb-3" style={{ fontFamily: 'var(--font-serif)' }}>
          {copy.modal_heading || 'Stay Connected'}
        </h2>
        <p className="text-gray-700 text-center mb-6">
          {copy.modal_body || 'Get exclusive updates, special offers, and be the first to know about new menu items and events.'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="newsletter-modal-form">
          <div>
            <label htmlFor="newsletter-name" className="block text-sm font-medium mb-2">Name</label>
            <input
              ref={nameInputRef}
              type="text"
              id="newsletter-name"
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Your name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black transition-all text-base"
              data-testid="newsletter-modal-name"
            />
          </div>

          <div>
            <label htmlFor="newsletter-email" className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              id="newsletter-email"
              name="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black transition-all text-base"
              data-testid="newsletter-modal-email"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-md font-semibold text-sm tracking-wide hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            data-testid="newsletter-modal-submit"
          >
            {submitting ? 'Subscribing…' : (copy.modal_button_label || 'Subscribe Now')}
          </button>

          {copy.modal_privacy_note && (
            <p className="text-xs text-gray-500 text-center">{copy.modal_privacy_note}</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default NewsletterModal;
